import { apiClient } from './api';
import { ENDPOINTS, STORAGE_KEYS } from './constants';

// Filter messages to show only the latest revisions for users
const filterMessagesForUser = (messages) => {
  if (!messages || !Array.isArray(messages)) return [];
  
  // Create a map to track which messages have been superseded
  const supersededMessages = new Set();
  const revisionMap = new Map();
  
  // First pass: identify all superseded messages and build revision map
  messages.forEach(message => {
    if (message.is_revision && message.revision_of_message_id && message.supersedes_visible) {
      supersededMessages.add(message.revision_of_message_id);
      revisionMap.set(message.revision_of_message_id, message);
    }
  });
  
  // Second pass: filter out superseded messages and replace with revisions
  const filteredMessages = [];
  const processedRevisions = new Set();
  
  messages.forEach(message => {
    // Skip if this message has been superseded
    if (supersededMessages.has(message.id)) {
      return;
    }
    
    // Skip if this is a revision that we've already processed
    if (message.is_revision && processedRevisions.has(message.id)) {
      return;
    }
    
    // If this is a revision, mark it as processed and include it
    if (message.is_revision) {
      processedRevisions.add(message.id);
    }
    
    filteredMessages.push(message);
  });
  
  // Sort by original creation time or current creation time
  return filteredMessages.sort((a, b) => {
    const timeA = new Date(a.original_message_created_at || a.created_at);
    const timeB = new Date(b.original_message_created_at || b.created_at);
    return timeA - timeB;
  });
};

const isAdmin = () => {
  const adminStatus = localStorage.getItem('is_admin');
  return adminStatus === 'true';
};

const logChatRequest = (method, userId, admin, endpoint) => {
};

export const chatService = {
  sendMessage: async (userInput, userId, timeZone = null, offsetMinutes = null, sessionId = null, newSession = false) => {
    const admin = isAdmin();
    const endpoint = admin ? ENDPOINTS.CHAT.ADMIN_AGENTS : ENDPOINTS.CHAT.AGENTS;
    logChatRequest('sendMessage', userId, admin, endpoint);
    
    try {
      const userTimezone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userOffsetMinutes = offsetMinutes !== null ? offsetMinutes : -new Date().getTimezoneOffset();
      
      const body = {
        message: userInput,
        user_input: userInput,
        time_zone: userTimezone,
        offset_minutes: userOffsetMinutes,
      };
  
      if (admin) {
        body.customer_user_id = '';
        if (sessionId) body.session_id = sessionId;
        if (newSession) body.new_session = newSession;
      } else {
        body.user_id = userId;
        if (sessionId) body.session_id = sessionId;
        if (newSession) body.new_session = newSession;
      }
      
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const config = {};
      if (token) {
        config.headers = { Authorization: token };
      }
      
      const response = await apiClient.post(endpoint, body, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  sendMultimodalMessage: async (formData, timeZone, offsetMinutes, userId, sessionId = null, newSession = false) => {
    const admin = isAdmin();
    const endpoint = admin ? ENDPOINTS.CHAT.ADMIN_MULTIMODAL : ENDPOINTS.CHAT.MULTIMODAL;
    logChatRequest('sendMultimodalMessage', userId, admin, endpoint);
    
    try {
      const userTimezone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userOffsetMinutes = offsetMinutes !== null ? offsetMinutes : -new Date().getTimezoneOffset();
      
      formData.append('time_zone', userTimezone);
      formData.append('offset_minutes', userOffsetMinutes);

      if (admin) {
        formData.append('customer_user_id', userId || '');
        if (sessionId) { formData.append('session_id', sessionId); formData.append('chat_id', sessionId); }
        if (newSession) formData.append('new_session', 'true');
      } else {
        formData.append('user_id', userId);
        if (sessionId) { formData.append('session_id', sessionId); formData.append('chat_id', sessionId); }
        if (newSession) formData.append('new_session', 'true');
      }
      
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (token) {
        headers.Authorization = token;
      }
      
      const response = await apiClient.post(endpoint, formData, { headers });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  streamMessage: async (userInput, userId) => {
    const admin = isAdmin();
    logChatRequest('streamMessage', userId, admin, ENDPOINTS.CHAT.STREAM);
    
    try {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers.Authorization = token;
      }
      
      const response = await fetch(`${apiClient.defaults.baseURL}${ENDPOINTS.CHAT.STREAM}?user_input=${encodeURIComponent(userInput)}`, {
        method: 'POST',
        headers,
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  },

  getUserChats: async (userId, limit = 20, offset = 0) => {
    const admin = isAdmin();
    // logChatRequest('getUserChats', userId, admin, ENDPOINTS.CHAT.CHATS);
    
    try {
      const params = {
        limit,
        offset,
        status: null,
        include_messages: true,
        messages_limit: 1,
        prioritize_unread: false
      };
      
      if (admin) {
        params.customer_user_id = userId || '';
      } else {
        params.user_id = userId;
      }
      
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const config = { params };
      if (token) {
        config.headers = { Authorization: token };
      }
    
      const response = await apiClient.get(ENDPOINTS.CHAT.CHATS, config);
      
      // Filter messages in chat previews for non-admin users
      if (!admin && response.data && response.data.data && response.data.data.chats) {
        response.data.data.chats = response.data.data.chats.map(chat => {
          if (chat.messages && Array.isArray(chat.messages)) {
            chat.messages = filterMessagesForUser(chat.messages);
          }
          return chat;
        });
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /** Fetch every chat page until the API reports no more results. */
  fetchAllUserChats: async (userId) => {
    const PAGE_SIZE = 50;
    let offset = 0;
    let allChats = [];
    let total = null;

    while (true) {
      const response = await chatService.getUserChats(userId, PAGE_SIZE, offset);
      const batch = response?.data?.chats ?? [];
      if (response?.data?.total != null) {
        total = response.data.total;
      }

      allChats = allChats.concat(batch);
      console.info(
        `[Dockie] fetchAllUserChats page offset=${offset} batch=${batch.length} loaded=${allChats.length} total=${total ?? "unknown"}`,
      );

      if (!batch.length) break;
      if (batch.length < PAGE_SIZE) break;
      if (total != null && allChats.length >= total) break;

      offset += batch.length;
    }

    return { chats: allChats, total: total ?? allChats.length };
  },

  createChat: async (userId, title = 'New Chat', metadata = null) => {
    const admin = isAdmin();
    logChatRequest('createChat', userId, admin, ENDPOINTS.CHAT.CHATS);
    
    try {
      const body = { title, metadata };
      
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const config = {};
      if (token) {
        config.headers = { Authorization: token };
      }
      
      const response = await apiClient.post(ENDPOINTS.CHAT.CHATS, body, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },



  getChatMessages: async (chatId, limit = 50, beforeId = null) => {
    const admin = isAdmin();
    const endpoint = `${ENDPOINTS.CHAT.CHATS}/${chatId}/messages`;
    logChatRequest('getChatMessages', chatId, admin, endpoint);
    
    try {
      const params = { limit };
      if (beforeId) {
        params.before_id = beforeId;
      }
      
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const config = { params };
      if (token) {
        config.headers = { Authorization: token };
      }
    
      const response = await apiClient.get(endpoint, config);
      
      // Filter messages for non-admin users to show only latest revisions
      if (!admin && response.data && Array.isArray(response.data.data)) {
        response.data.data = filterMessagesForUser(response.data.data);
      } else if (!admin && Array.isArray(response.data)) {
        response.data = filterMessagesForUser(response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('getChatMessages error:', error.response?.status, error.response?.data);
      throw error;
    }
  },

  pollForPaymentConfirmation: async (sessionId, onConfirmation, onPaymentConfirmed) => {
    let paymentConfirmed = false;
    let bookingConfirmed = false;
    const admin = isAdmin();
    
    const pollInterval = setInterval(async () => {
      try {
        // normalize result from getChatMessages(): some callers return
        // the axios response object, others return response.data directly.
        const result = await chatService.getChatMessages(sessionId, 50);
        let messages = [];

        if (Array.isArray(result)) {
          messages = result;
        } else if (Array.isArray(result?.data)) {
          messages = result.data;
        } else if (Array.isArray(result?.messages)) {
          messages = result.messages;
        } else if (Array.isArray(result?.data?.messages)) {
          messages = result.data.messages;
        } else if (Array.isArray(result?.chats)) {
          // fallback if chats object contains messages
          messages = result.chats;
        } else {
          messages = [];
        }

        // Apply message filtering for non-admin users
        if (!admin) {
          messages = filterMessagesForUser(messages);
        }

        if (!paymentConfirmed) {
          const paymentMsg = messages.find(
            (msg) =>
              msg.is_agent_message &&
              msg.metadata?.system_message_type === "payment_confirmation"
          );

          if (paymentMsg) {
            paymentConfirmed = true;
            if (onPaymentConfirmed) onPaymentConfirmed(paymentMsg);
          }
        }

        if (!bookingConfirmed) {
          const bookingMsg = messages.find(
            (msg) =>
              msg.is_agent_message &&
              msg.metadata?.system_message_type === "booking_confirmation"
          );

          if (bookingMsg) {
            bookingConfirmed = true;
            onConfirmation(bookingMsg);
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000);
    
    setTimeout(() => clearInterval(pollInterval), 120000);
    return () => clearInterval(pollInterval);
  },

  getShipmentDetailsByChatID: async (chatId) => {
    const admin = isAdmin();
    const endpoint = `${ENDPOINTS.SHIPMENTS.BY_CHAT}/${chatId}`;
    logChatRequest('getShipmentDetailsByChatID', chatId, admin, endpoint);
    
    try {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const config = {};
      if (token) {
        config.headers = { Authorization: token };
      }
      
      const response = await apiClient.get(endpoint, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  pollChatList: (userId, onUpdate, interval = 60000) => {
    let lastChatData = null;
    let inFlight = false;
    const admin = isAdmin();
    
    const poll = async () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const response = await chatService.getUserChats(userId, 15, 0);
        let currentChats = response?.data?.chats || response || [];
        
        if (!admin && Array.isArray(currentChats)) {
          currentChats = currentChats.map(chat => {
            if (chat.messages && Array.isArray(chat.messages)) {
              chat.messages = filterMessagesForUser(chat.messages);
            }
            return chat;
          });
        }
        
        const serialized = JSON.stringify(currentChats);
        if (lastChatData !== serialized) {
          lastChatData = serialized;
          onUpdate([], currentChats);
        }
      } catch (error) {
        console.error('Chat list polling error:', error);
      } finally {
        inFlight = false;
      }
    };
    
    const pollInterval = setInterval(poll, interval);
    return () => clearInterval(pollInterval);
  },
};